import React, { useState, ReactNode, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import s from './s.module.less';
import { Button, Space, Divider, Tag } from 'antd';

interface FormCardProps {
    title?: string;
    isOptional?: boolean;
    children: ((isEditing: boolean) => ReactNode);
    type: 'edit' | 'add' | 'review';
    showHeader?: boolean;
    onSave?: () => void;
    hasContent?: boolean;
}

const FormCard: React.FC<FormCardProps> = forwardRef(({
    title,
    isOptional,
    children,
    type = 'edit',
    showHeader = true,
    onSave,
    hasContent = false,
}, ref) => {
    const [isEditing, setIsEditing] = useState(false);
    const [collapsed, setCollapsed] = useState(type === 'add');
    const childRef = useRef(null);

    useEffect(() => {
        if (type === 'edit') {
            setIsEditing(true);
        }
        if (type === 'add' && hasContent) {
            setCollapsed(false);
        }
    }, [type, hasContent]);

    const handleSave = async () => {
        try {
            if (childRef.current && childRef.current.onSubmit) {
                await childRef.current.onSubmit();
                setIsEditing(false);
                if (type === 'add' && !hasContent) {
                    setCollapsed(true);
                }
                onSave?.();
            }
        } catch (error) {
            console.error('Form save failed:', error);
        }
    };

    const renderEditButton = () => (
        <Button
            onClick={() => {
                setIsEditing(true);
            }}
            className={s.editButton}
        >
            Edit
        </Button>
    );

    const renderActionButtons = () => (
        <Space>
            <Button
                onClick={() => {
                    setIsEditing(false);
                    if (type === 'add' && !hasContent) {
                        setCollapsed(true);
                    }
                }}
            >
                Cancel
            </Button>
            <Button
                type="primary"
                onClick={handleSave}
                className={s.saveButton}
            >
                Save changes
            </Button>
        </Space>
    );

    const renderCollapsedButton = () => {
        if (hasContent) return renderEditButton();

        return (
            <Button
                onClick={() => {
                    setCollapsed(false);
                    setIsEditing(true);
                }}
                className={s.collapseButton}
                type="text"
            >
                Add
            </Button>
        );
    };

    const reviewButton = isEditing ? renderActionButtons() : renderEditButton();
    const addButton = isEditing ? renderActionButtons() : renderCollapsedButton();

    // 根据 isEditing 状态渲染子组件
    const renderChildren = () => {
        const childElement = children(isEditing);
        if (React.isValidElement(childElement)) {
            return React.cloneElement(childElement as React.ReactElement, { ref: childRef });
        }
        return childElement;
    };

    useImperativeHandle(ref, () => ({
        onSubmit: childRef.current?.onSubmit,
        onValidate: () => {
            if (childRef.current && childRef.current.onValidate) {
                return childRef.current.onValidate();
            }
            return Promise.resolve();
        },
        isEditing,
    }));

    return (
        <div className={s.formCard} style={{ padding: collapsed ? '16px 24px' : '24px' }}>
            {showHeader && title && (
                <>
                    <div className={s.formCardHeader}>
                        <div className="flex items-center gap-2">
                            <div className={s.formCardTitle}>{title}</div>
                            {isOptional && (
                                <Tag style={{ backgroundColor: '#E5E7EB', color: '#1F2937', border: 'none' }}>Optional</Tag>
                            )}
                        </div>
                        {type === 'review' ? reviewButton : null}
                        {type === 'add' ? addButton : null}
                    </div>
                    {type === 'review' && <Divider className="mt-2 mb-6" />}
                </>
            )}
            {(!collapsed || hasContent) && (
                <div className={s.formCardContent}>
                    {renderChildren()}
                </div>
            )}
        </div>
    );
});

export default FormCard;
